/**
 * Iter 227 · Wave 3 admin console — ABDM · Payouts · Adverse Events · Reviews
 */
import React, { useEffect, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  LinkSimple, Coins, Warning, Star, PlayCircle, Download,
  Plus, ArrowClockwise,
} from "@phosphor-icons/react";

const TABS = [
  { key: "abdm",     label: "ABDM · ABHA",   icon: LinkSimple },
  { key: "payouts",  label: "Payouts",       icon: Coins },
  { key: "ae",       label: "Adverse Events", icon: Warning },
  { key: "reviews",  label: "Reviews & NPS", icon: Star },
];

export default function Wave3Console() {
  const [tab, setTab] = useState("abdm");
  return (
    <AppShell title="Wave 3" subtitle="ABDM · Payouts · Adverse Events · Reviews">
      <div className="space-y-4" data-testid="wave3-root">
        <div className="flex items-center gap-2 border-b border-[#E5E2D9] overflow-x-auto">
          {TABS.map(t => {
            const Icon = t.icon; const active = tab === t.key;
            return (
              <button key={t.key} onClick={() => setTab(t.key)}
                data-testid={`wave3-tab-${t.key}`}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap ${
                  active ? "border-[#B91C1C] text-[#B91C1C]" : "border-transparent text-[#5C5A55]"
                }`}>
                <Icon size={14} weight={active?"fill":"regular"}/>{t.label}
              </button>
            );
          })}
        </div>
        {tab === "abdm"    && <AbdmTab/>}
        {tab === "payouts" && <PayoutsTab/>}
        {tab === "ae"      && <AeTab/>}
        {tab === "reviews" && <ReviewsTab/>}
      </div>
    </AppShell>
  );
}

// ---- ABDM ---------------------------------------------------------
function AbdmTab() {
  const [links, setLinks] = useState([]);
  const [pushes, setPushes] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const load = async () => {
    const [l, p] = await Promise.all([
      api.get("/health-services/abdm/links"),
      api.get("/health-services/abdm/pushes?days=30"),
    ]);
    setLinks(l.data.links || []); setPushes(p.data.pushes || []);
  };
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-4" data-testid="abdm-tab">
      <div className="p-4 rounded-lg bg-[#DBEAFE] border border-[#93C5FD] text-sm text-[#1E3A8A]">
        <b>ABDM · ABHA</b> · Link a patient's Health ID to their profile · push consult notes,
        prescriptions and lab reports to their PHR with consent.
      </div>
      <div className="flex items-center justify-between">
        <div className="text-sm text-[#5C5A55]">{links.length} linked citizens · {pushes.length} PHR pushes (30d)</div>
        <button onClick={() => setShowAdd(true)} data-testid="abha-add"
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[#1D4ED8] text-white rounded-md">
          <Plus size={14}/> Link ABHA
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <MiniList title="Linked citizens" rows={links.slice(0,10).map(l => ({
          left: l.customer_id.slice(0,8), right: l.abha_id, sub: l.status,
        }))}/>
        <MiniList title="Recent PHR pushes" rows={pushes.slice(0,10).map(p => ({
          left: p.doc_type, right: p.customer_id.slice(0,8), sub: p.status,
        }))}/>
      </div>
      {showAdd && <AbhaLinkModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }}/>}
    </div>
  );
}

function AbhaLinkModal({ onClose, onSaved }) {
  const [f, setF] = useState({customer_id:"", abha_id:"", consent_purpose:"care_mgmt"});
  const submit = async () => {
    try { await api.post("/health-services/abdm/link", f); toast.success("Linked"); onSaved(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Modal onClose={onClose} title="Link ABHA">
      <input placeholder="Customer ID" value={f.customer_id} data-testid="abha-cust"
              onChange={e => setF({...f, customer_id:e.target.value})} className="w-full border rounded px-3 py-2 mb-2"/>
      <input placeholder="ABHA (14 digits or xx-xxxx-xxxx-xxxx)" value={f.abha_id} data-testid="abha-id"
              onChange={e => setF({...f, abha_id:e.target.value})} className="w-full border rounded px-3 py-2 mb-2"/>
      <button onClick={submit} data-testid="abha-save"
              className="w-full py-2 rounded bg-[#1D4ED8] text-white">Link</button>
    </Modal>
  );
}

// ---- Payouts ------------------------------------------------------
function PayoutsTab() {
  const [runs, setRuns] = useState([]);
  const [selected, setSelected] = useState(null);
  const [rows, setRows] = useState([]);
  const load = async () => {
    const r = await api.get("/health-services/payouts/runs?days=90");
    setRuns(r.data.runs || []);
  };
  useEffect(() => { load(); }, []);
  const runNew = async () => {
    const to = new Date().toISOString().slice(0,10);
    const from = new Date(Date.now() - 7*86400000).toISOString().slice(0,10);
    try {
      await api.post("/health-services/payouts/run", {from_date:from, to_date:to});
      toast.success("Run queued"); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const openRun = async (run_id) => {
    setSelected(run_id);
    const r = await api.get(`/health-services/payouts/runs/${run_id}`);
    setRows(r.data.rows || []);
  };
  const pdfUrl = (run_id, provider_id) =>
    `${process.env.REACT_APP_BACKEND_URL}/api/health-services/payouts/runs/${run_id}/partner/${provider_id}/pdf`;

  const downloadPdf = async (run_id, provider_id, name) => {
    try {
      const r = await fetch(pdfUrl(run_id, provider_id), {
        headers: {"Authorization": `Bearer ${localStorage.getItem("rescigo_token")}`}});
      if (!r.ok) throw new Error(await r.text());
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `payout_${name || provider_id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="payouts-tab">
      <div className="p-4 rounded-lg bg-[#FEF3C7] border border-[#FCD34D] text-sm text-[#7A5A11] flex items-start justify-between gap-3">
        <div>
          <b>Provider payouts</b> · Runs aggregate completed HSRs into per-partner
          gross → commission (12%) → TDS (2%) → GST → net-payable rows. Mark each
          `queued → processed` once Razorpay Route transfer clears.
        </div>
        <button onClick={runNew} data-testid="payouts-run"
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[#B45309] text-white rounded-md whitespace-nowrap">
          <PlayCircle size={14}/> Run last 7d
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="bg-white rounded-lg border p-4">
          <div className="text-xs uppercase tracking-wider text-[#8A8780] mb-2">Runs (90d)</div>
          {runs.length === 0 ? <div className="text-sm text-[#8A8780]">No runs yet</div>
            : runs.map(r => (
              <button key={r._id} onClick={() => openRun(r._id)} data-testid={`payout-run-${r._id.slice(0,6)}`}
                       className={`w-full text-left py-2 border-b last:border-0 ${selected===r._id?"bg-[#F5F3EE]":""}`}>
                <div className="flex items-center justify-between text-sm">
                  <span className="font-mono text-[11px]">{r._id.slice(0,8)}…</span>
                  <span className="font-medium">₹{Number(r.total_gross||0).toLocaleString("en-IN")}</span>
                </div>
                <div className="text-[11px] text-[#8A8780]">
                  {r.from_date} → {r.to_date} · {r.rows} providers · {r.processed}/{r.rows} paid
                </div>
              </button>
            ))}
        </div>
        <div className="bg-white rounded-lg border p-4">
          <div className="text-xs uppercase tracking-wider text-[#8A8780] mb-2">Provider breakdown</div>
          {!selected ? <div className="text-sm text-[#8A8780]">Pick a run</div>
            : rows.length === 0 ? <div className="text-sm text-[#8A8780]">No rows in this run</div>
            : rows.map(r => (
              <div key={r.provider_id} data-testid={`payout-partner-${r.provider_id?.slice(0,6)}`}
                    className="flex items-center justify-between py-2 border-b last:border-0">
                <div>
                  <div className="text-sm font-medium">{r.provider_name || r.provider_id.slice(0,8)}</div>
                  <div className="text-[11px] text-[#8A8780]">₹{r.gross} gross · ₹{r.commission} comm · ₹{r.tds} TDS</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm">₹{r.net_payable}</span>
                  <button onClick={() => downloadPdf(selected, r.provider_id, r.provider_name)}
                           data-testid={`payout-pdf-${r.provider_id?.slice(0,6)}`}
                           className="p-1.5 rounded bg-[#4338CA] text-white">
                    <Download size={12}/>
                  </button>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

// ---- Adverse events ----------------------------------------------
function AeTab() {
  const [events, setEvents] = useState([]);
  const [buckets, setBuckets] = useState({});
  useEffect(() => {
    api.get("/health-services/adverse-events?days=90")
      .then(r => { setEvents(r.data.events || []); setBuckets(r.data.by_status || {}); });
  }, []);
  const SEV = {near_miss:"bg-[#DBEAFE] text-[#1D4ED8]", no_harm:"bg-[#D4E0D1] text-[#1A3317]",
                 minor:"bg-[#FEF3C7] text-[#B45309]", moderate:"bg-[#FED7AA] text-[#C2410C]",
                 severe:"bg-[#FEE2E2] text-[#B91C1C]", sentinel:"bg-[#7F1D1D] text-white"};
  return (
    <div className="space-y-4" data-testid="ae-tab">
      <div className="grid grid-cols-5 gap-2">
        {["reported","under_review","rca_done","action_taken","closed"].map(s => (
          <div key={s} className="p-3 bg-white rounded border text-center">
            <div className="text-2xl font-heading">{buckets[s] || 0}</div>
            <div className="text-[10px] uppercase tracking-wider text-[#8A8780]">{s.replace(/_/g," ")}</div>
          </div>
        ))}
      </div>
      <div className="bg-white rounded-lg border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F5F3EE] text-[#5C5A55] text-[11px] uppercase tracking-wider">
            <tr><th className="text-left px-4 py-3">AE#</th><th className="text-left px-4 py-3">Description</th>
                <th className="text-center px-4 py-3">Severity</th><th className="text-center px-4 py-3">Status</th></tr>
          </thead>
          <tbody className="divide-y divide-[#F0EDE4]">
            {events.length === 0 && <tr><td colSpan={4} className="text-center py-6 text-[#8A8780]">No adverse events</td></tr>}
            {events.map(e => (
              <tr key={e.id} data-testid={`ae-row-${e.ae_no}`}>
                <td className="px-4 py-3 font-mono text-[12px]">{e.ae_no}</td>
                <td className="px-4 py-3">{(e.description||"").slice(0,80)}</td>
                <td className="px-4 py-3 text-center"><span className={`px-2 py-0.5 rounded-full text-[11px] ${SEV[e.severity]||""}`}>{e.severity}</span></td>
                <td className="px-4 py-3 text-center text-[11px]">{e.status?.replace(/_/g," ")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---- Reviews & NPS -----------------------------------------------
function ReviewsTab() {
  const [reviews, setReviews] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [log, setLog] = useState([]);
  const load = async () => {
    const [rv, tk, lb, lg] = await Promise.all([
      api.get("/health-services/reviews?days=30"),
      api.get("/health-services/reviews/tickets?status=open"),
      api.get("/health-services/reviews/leaderboard?days=30"),
      api.get("/health-services/reviews/prompts/log?days=3"),
    ]);
    setReviews(rv.data); setTickets(tk.data.tickets || []);
    setLeaderboard(lb.data.leaderboard || []); setLog(lg.data.log || []);
  };
  useEffect(() => { load(); }, []);
  const runPromptsNow = async () => {
    try {
      const r = await api.post("/health-services/reviews/prompts/run-now");
      toast.success(`Queued ${r.data.queued} WhatsApp prompt(s)`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-4" data-testid="reviews-tab">
      <div className="grid grid-cols-4 gap-3">
        <StatTile label="Avg rating" v={reviews?.avg_rating || "—"} c="#B45309"/>
        <StatTile label="NPS" v={reviews?.nps ?? "—"} c="#065F46"/>
        <StatTile label="Open tickets" v={tickets.length} c="#B91C1C"/>
        <StatTile label="WhatsApp prompts (3d)" v={log.length} c="#4338CA"/>
      </div>
      <div className="p-4 rounded-lg bg-[#D1FAE5] border border-[#6EE7B7] text-sm text-[#065F46] flex items-start justify-between gap-3">
        <div>
          <b>WhatsApp review prompt cron</b> · runs every 15 min · targets HSRs completed 2-4 h ago with a
          one-tap link to <code>/me/reviews?hsr=…</code>. Marks each HSR with <code>review_prompt_sent_at</code> to prevent duplicates.
        </div>
        <button onClick={runPromptsNow} data-testid="reviews-prompt-run"
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[#065F46] text-white rounded-md whitespace-nowrap">
          <ArrowClockwise size={14}/> Run now
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-white rounded-lg border p-4">
          <div className="text-xs uppercase tracking-wider text-[#8A8780] mb-2">Distribution</div>
          {[5,4,3,2,1].map(r => (
            <div key={r} className="flex items-center gap-2 py-1 text-sm">
              <span className="w-4 text-right">{r}⭐</span>
              <div className="flex-1 bg-[#F5F3EE] rounded-full h-2 overflow-hidden">
                <div className="h-full bg-[#B45309]" style={{width: `${Math.min(100, (reviews?.distribution?.[r]||0) * 10)}%`}}/>
              </div>
              <span className="text-[11px] text-[#8A8780] w-8">{reviews?.distribution?.[r]||0}</span>
            </div>
          ))}
        </div>
        <div className="bg-white rounded-lg border p-4">
          <div className="text-xs uppercase tracking-wider text-[#8A8780] mb-2">Provider leaderboard</div>
          {leaderboard.slice(0,8).map(r => (
            <div key={r.provider_id} className="flex items-center justify-between py-1 text-sm">
              <span className="font-mono text-[11px]">{r.provider_id?.slice(0,10)}</span>
              <span className="font-medium">{r.avg_rating}⭐ ({r.reviews})</span>
            </div>
          ))}
          {leaderboard.length === 0 && <div className="text-sm text-[#8A8780]">No data</div>}
        </div>
        <div className="bg-white rounded-lg border p-4">
          <div className="text-xs uppercase tracking-wider text-[#8A8780] mb-2">Open tickets</div>
          {tickets.slice(0,8).map(t => (
            <div key={t.id} className="py-1 text-sm border-b last:border-0">
              <div className="font-mono text-[11px]">{t.id.slice(0,10)}</div>
              <div className="text-[11px] text-[#B91C1C]">{t.reason}</div>
            </div>
          ))}
          {tickets.length === 0 && <div className="text-sm text-[#8A8780]">No open tickets</div>}
        </div>
      </div>
    </div>
  );
}

// ---- helpers ------------------------------------------------------
function MiniList({ title, rows }) {
  return (
    <div className="bg-white rounded-lg border p-4">
      <div className="text-xs uppercase tracking-wider text-[#8A8780] mb-2">{title}</div>
      {rows.length === 0 ? <div className="text-sm text-[#8A8780]">Empty</div>
        : rows.map((r,i) => (
          <div key={i} className="flex items-center justify-between py-1 text-sm border-b last:border-0">
            <span className="font-mono text-[11px]">{r.left}</span>
            <span>{r.right}</span>
            {r.sub && <span className="text-[11px] text-[#8A8780]">{r.sub}</span>}
          </div>
        ))}
    </div>
  );
}
function StatTile({ label, v, c }) {
  return (
    <div className="p-4 bg-white rounded-lg border">
      <div className="text-[24px] font-heading font-semibold" style={{color:c}}>{v}</div>
      <div className="text-[11px] uppercase tracking-[0.15em] text-[#8A8780] mt-1">{label}</div>
    </div>
  );
}
function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-md w-full p-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-lg">{title}</h2>
          <button onClick={onClose}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}
